import React, { useEffect, useState } from "react";
import { FaEye, FaRegHeart } from "react-icons/fa";
import { RiShoppingCartLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { add_to_card, add_to_wishlist, messageClear } from "../../store/reducers/cardReducer";
import toast from "react-hot-toast";
import useGuestId from "../../hooks/useGuestId";
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, Box, Typography } from "@mui/material";
import { useTheme } from "../../context/ThemeContext";
import { motion } from "framer-motion";

const ShopProducts = ({ styles, products }) => {
  const dispatch = useDispatch();
  const guestId = useGuestId();

  const { userInfo } = useSelector(state => state.auth);
  const { errorMessage, successMessage } = useSelector(state => state.card);

  const [quantity] = useState(1);
  const [openSizeDialog, setOpenSizeDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(null);

  const { getBackground, getBorder, getMuiButtonStyle, getMuiTextStyle, getMuiBackground, getColor, getButtonStyles } = useTheme();

  const getVariantColorBackground = colorCode => {
    if (!colorCode) return "#ccc";
    const colors = colorCode.split(",");

    if (colors.length === 1) return colors[0];
    if (colors.length === 2) return `linear-gradient(to right, ${colors.join(", ")})`;
    if (colors.length === 3) return `linear-gradient(to right, ${colors.join(", ")})`;
    return "#ccc";
  };

  // Función para aplanar productos si es necesario
  const getFlattenedProducts = () => {
    if (!products || !Array.isArray(products)) return [];

    // Si products es un array de arrays, lo aplanamos
    if (Array.isArray(products[0])) {
      return products.flat().filter(p => p && typeof p === "object" && !Array.isArray(p));
    }

    // Si ya es un array simple, lo retornamos tal cual
    return products.filter(p => p && typeof p === "object" && !Array.isArray(p));
  };

  // Función corregida para obtener imagen principal
  const getMainImage = product => {
    if (!product || typeof product !== "object") {
      return "https://via.placeholder.com/280x280?text=Imagen+No+Disponible";
    }

    // Verificar si hay variantes
    if (!product.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
      return product.images && Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : "https://via.placeholder.com/280x280?text=Imagen+No+Disponible";
    }

    const primeraVariante = product.variants[0];

    if (primeraVariante.colorImages && Array.isArray(primeraVariante.colorImages) && primeraVariante.colorImages.length > 0) {
      return primeraVariante.colorImages[0];
    }

    // Verificar que sizes exista y sea array antes de usar find
    if (primeraVariante.sizes && Array.isArray(primeraVariante.sizes)) {
      const tallaConStock = primeraVariante.sizes.find(size => size && size.stock > 0);
      if (tallaConStock && tallaConStock.image) {
        return tallaConStock.image;
      }
    }

    // Fallback a images del producto
    return product.images && Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : "https://via.placeholder.com/280x280?text=Imagen+No+Disponible";
  };

  // Segunda imagen para efecto hover swap
  // Regla de oro: con variante → segunda colorImage o segunda imagen de talla
  // Sin variante → segunda imagen del array images
  const getSecondImage = product => {
    if (!product) return null;
    if (product.variants?.length > 0) {
      const v = product.variants[0];
      if (v.colorImages?.length > 1) return v.colorImages[1];
      if (v.colorImages?.length === 1) return v.colorImages[0];
    }
    if (product.images?.length > 1) return product.images[1];
    return null;
  };

  // Regla de oro precios:
  // Con variante → precio/descuento de primera talla con stock
  // Sin variante → precio/descuento directo del producto
  const getPriceInfo = product => {
    if (!product) return { original: 0, display: 0, hasDiscount: false };
    if (product.variants?.length > 0) {
      const sizes = product.variants[0]?.sizes || [];
      const size = sizes.find(s => s.stock > 0) || sizes[0];
      if (size) {
        const hasDiscount = size.discount > 0 && size.discount < size.price;
        return { original: size.price || 0, display: hasDiscount ? size.discount : size.price || 0, hasDiscount };
      }
    }
    const hasDiscount = product.discount > 0 && product.discount < product.price;
    return { original: product.price || 0, display: hasDiscount ? product.discount : product.price || 0, hasDiscount };
  };

  const add_card = product => {
    // Si el producto tiene variantes (tallas), mostrar el diálogo
    if (product.variants && product.variants.length > 0) {
      setSelectedProduct(product);
      setSelectedVariant(product.variants[0]);
      setOpenSizeDialog(true);
      return;
    }
    // Si no tiene variantes, agregar directamente al carrito
    const payload = {
      quantity,
      productId: product._id,
      variant: null,
    };

    if (userInfo) {
      payload.userId = userInfo.id;
    } else if (guestId) {
      payload.userId = guestId;
    }
    dispatch(add_to_card(payload));
  };

  // Cuando le da aceptar el boton de tallas
  const handleAddToCartWithSize = () => {
    if (!selectedSize) {
      alert("Por favor selecciona una talla");
      return;
    }

    const sizeObj = selectedVariant.sizes.find(s => s.size === selectedSize);

    if (!sizeObj || sizeObj.stock <= 0) {
      alert("No hay suficiente stock para esta talla");
      return;
    }

    // Crear el objeto variantString que se almacenará en tu modelo
    const variantString = JSON.stringify({
      color: selectedVariant.color,
      colorCode: selectedVariant.colorCode,
      size: sizeObj.size,
      sizeId: sizeObj._id,
      price: sizeObj.price,
      discount: sizeObj.discount,
      sku: sizeObj.sku,
    });

    const payload = {
      productId: selectedProduct._id,
      quantity: 1,
      variant: variantString, // Almacenamos como string
    };

    if (userInfo) {
      payload.userId = userInfo.id;
    } else if (guestId) {
      payload.userId = guestId;
    }

    dispatch(add_to_card(payload));

    setOpenSizeDialog(false);
    setSelectedVariant("");
    setSelectedSize("");
  };

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage, dispatch]);

  const add_wishlist = pro => {
    dispatch(
      add_to_wishlist({
        userId: userInfo && userInfo.id ? userInfo.id : "",
        productId: pro._id,
        name: pro.name,
        price: pro.price,
        image: pro.images[0],
        discount: pro.discount,
        rating: pro.rating,
        slug: pro.slug,
      })
    );
  };

  const flattenedProducts = getFlattenedProducts();

  return (
    <div
      className={`w-full grid ${
        styles === "grid"
          ? `grid-cols-4 md-lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2 xs:grid-cols-2`
          : "grid-cols-1"
      } gap-2 sm:gap-1 p-0`}>
      {flattenedProducts.map((p, i) => {
        const { original: originalPrice, display: displayPrice, hasDiscount } = getPriceInfo(p);
        const mainImg   = getMainImage(p);
        const secondImg = getSecondImage(p);

        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: i * 0.04 }}
            whileHover={{ y: -4, boxShadow: "0 12px 32px 0 rgba(174,29,34,0.18), 0 4px 12px 0 rgba(0,0,0,0.10)" }}
            style={{ ...getBackground("surface"), ...getBorder("borderCatProd") }}
            className={`flex transition-all duration-300 ${
              styles === "grid" ? "flex-col h-full" : "flex-col md-lg:flex-row h-auto"
            } w-full rounded-md overflow-hidden border`}>

            {/* Imagen con swap al hover */}
            <div className={`${
              styles === "grid"
                ? "w-full h-[200px] xs:h-[250px] sm:h-[250px] md:h-[177px] lg:h-[180px] xl:h-[180px] 2xl:h-[140px]"
                : "w-full md:w-1/3 h-[150px] md:h-full"
            } relative group overflow-hidden`}>

              <Link to={`/product/details/${p.slug}`} className="block w-full h-full">
                {/* Imagen principal */}
                <img
                  className="w-full h-full object-contain transition-all duration-500 group-hover:opacity-0"
                  src={mainImg}
                  alt={p.name}
                  onError={e => { e.target.src = "https://via.placeholder.com/280x280?text=Imagen+No+Disponible"; }}
                />
                {/* Segunda imagen (swap) */}
                {secondImg && (
                  <img
                    className="absolute top-0 left-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-all duration-500"
                    src={secondImg}
                    alt={`${p.name} - vista 2`}
                    aria-hidden="true"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
              </Link>

              {/* Badge descuento */}
              {hasDiscount && (
                <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded z-10">
                  -{Math.round(((originalPrice - displayPrice) / originalPrice) * 100)}%
                </div>
              )}

              {/* Botones flotantes */}
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                <button onClick={() => add_wishlist(p)} className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-[#FF3A59] hover:text-white">
                  <FaRegHeart className="text-xs" />
                </button>
                <Link to={`/product/details/${p.slug}`} className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-[#FF3A59] hover:text-white">
                  <FaEye className="text-xs" />
                </Link>
                <button onClick={() => add_card(p)} className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-[#FF3A59] hover:text-white">
                  <RiShoppingCartLine className="text-xs" />
                </button>
              </div>
            </div>

            {/* Contenido texto + precio */}
            <div className={`${
              styles === "grid" ? "p-2" : "p-2 md-lg:flex-1 md-lg:flex md-lg:flex-col md-lg:justify-between"
            } flex flex-col`}>
              <div>
                <h3 style={getMuiTextStyle("textdescriptionProduct")} className="font-medium text-[11px] leading-tight line-clamp-2 tracking-tight">
                  {p.name}
                </h3>
                <div className="flex items-center justify-between mt-1 pr-2 pl-2">
                  <span style={getMuiTextStyle("textdiscountProduct")} className="font-sans font-bold text-[15px] leading-none">
                    S/{displayPrice.toFixed(2)}
                  </span>
                  <div className="flex items-center gap-1">
                    {hasDiscount && (
                      <span style={getMuiTextStyle("textpriceProduct")} className="font-sans text-[11px] leading-none line-through opacity-70">
                        S/{originalPrice.toFixed(2)}
                      </span>
                    )}
                    {/* Botón carrito móvil - al lado del precio */}
                    <button
                      onClick={() => add_card(p)}
                      className="md:flex lg:hidden xl:hidden 2xl:hidden w-7 h-7 rounded-full border border-[#ae1d22] bg-white flex items-center justify-center flex-shrink-0"
                      aria-label="Agregar al carrito">
                      <RiShoppingCartLine size={13} color="#ae1d22" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-1 flex items-center">
                {styles !== "grid" && (
                  <button onClick={() => add_card(p)} style={getButtonStyles("primary", "white")} className="text-xs xs:text-sm px-2 py-1 rounded hover:bg-blue-700">
                    Añadir
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        );
      })}

      <Dialog
        open={openSizeDialog}
        onClose={() => {
          setSelectedVariant("");
          setSelectedSize("");
          setOpenSizeDialog(false);
        }}
        PaperProps={{
          sx: {
            ...getMuiBackground("surface"),
            minWidth: "400px",
            borderRadius: "8px",
          },
        }}>
        <DialogTitle sx={getMuiTextStyle("textdescriptionProduct")}>Selecciona tu talla</DialogTitle>
        <DialogContent>
          {/* Mostrar el color seleccionado */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mr: 1, ...getMuiTextStyle("textdescriptionProduct") }}>
              Color:
            </Typography>
            <Box
              sx={{
                width: 20,
                height: 20,
                background: getVariantColorBackground(selectedVariant?.colorCode),
                border: `1px solid ${getColor("border", "#dee2e6")}`,
                borderRadius: "50%",
                display: "inline-block",
              }}
            />
            <Typography variant="body1" sx={{ ml: 1 }}>
              {selectedVariant?.color || "Color no disponible"}
            </Typography>
          </Box>

          {/* Selector de tallas como botones */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ ml: 1, ...getMuiTextStyle("textdescriptionProduct") }}>
              Tallas disponibles
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedVariant?.sizes?.map(sizeObj => {
                const isOutOfStock = sizeObj.stock <= 0;
                const isSelected = selectedSize === sizeObj.size;

                return (
                  <Button
                    key={sizeObj._id}
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => !isOutOfStock && setSelectedSize(sizeObj.size)}
                    disabled={isOutOfStock}
                    sx={{
                      minWidth: 0,
                      width: 40,
                      height: 40,
                      position: "relative",
                      fontSize: "0.875rem",
                      fontWeight: isSelected ? 600 : 400,

                      // Aplicar estilos del theme
                      ...(isSelected && getMuiButtonStyle("primary", "contained")),
                      ...(!isSelected && !isOutOfStock && getMuiButtonStyle("primary", "outlined")),
                      // Estilos para deshabilitado
                      ...(isOutOfStock && {
                        color: getColor("textSecondary", "#6c757d"),
                        borderColor: getColor("border", "#dee2e6"),
                        backgroundColor: getColor("surface", "#f8f9fa"),
                        "&:hover": { backgroundColor: getColor("surface", "#f8f9fa") },
                      }),
                    }}
                    aria-label={`Talla ${sizeObj.size}${isOutOfStock ? " - Agotado" : ""}`}>
                    {sizeObj.size}
                    {isOutOfStock && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          width: "80%",
                          height: 1,
                          backgroundColor: getColor("danger", "#dc3545"),
                          transform: "translate(-50%, -50%) rotate(45deg)",
                        }}
                      />
                    )}
                  </Button>
                );
              })}
            </Box>
            {!selectedSize && (
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  display: "block",
                  color: getColor("danger", "#dc3545"),
                }}>
                Por favor selecciona una talla
              </Typography>
            )}
          </Box>
          {/* Mostrar precio de la talla seleccionada */}

          {selectedSize &&
            (() => {
              const sizeInfo = selectedVariant.sizes.find(s => s.size === selectedSize);
              return (
                <Box sx={{ mt: 2 }}>
                  {sizeInfo?.discount > 0 ? (
                    <>
                      <Typography variant="h6" sx={{ textDecoration: "line-through", color: "text.disabled" }}>
                        S/ {sizeInfo.price.toFixed(2)}
                      </Typography>
                      <Typography variant="h6" sx={{ color: getColor("textdiscountProduct", "#28a745") }}>
                        S/ {sizeInfo.discount.toFixed(2)}
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: "0.8rem",
                            color: getColor("textpercentdsctProduct", "#28a745"),
                          }}>
                          ({Math.round(((sizeInfo.price - sizeInfo.discount) / sizeInfo.price) * 100)}%)
                        </Box>
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="h6" sx={getMuiTextStyle("textpriceProduct")}>
                      S/ {sizeInfo?.price.toFixed(2)}
                    </Typography>
                  )}
                </Box>
              );
            })()}
        </DialogContent>
        <DialogActions
          sx={{
            ...getMuiBackground("text:"),
            p: 2,
            gap: 1,
          }}>
          <Button onClick={() => setOpenSizeDialog(false)} sx={getMuiButtonStyle("secondary", "outlined")}>
            Cancelar
          </Button>
          <Button onClick={handleAddToCartWithSize} variant="contained" disabled={!selectedSize} sx={getMuiButtonStyle("primary", "contained")}>
            Agregar al carrito
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ShopProducts;
